import { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

function eur(n) {
  return n.toLocaleString("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
}

// Compound a monthly-savings + lump portfolio with net rate (nominal - TER)
function project(lump, monthly, years, nominal, ter) {
  const netAnnual = nominal - ter;
  const monthlyRate = netAnnual / 100 / 12;
  const rows = [];
  let capital = lump;
  for (let y = 1; y <= years; y++) {
    for (let m = 0; m < 12; m++) capital = (capital + monthly) * (1 + monthlyRate);
    rows.push({ year: y, value: Math.round(capital) });
  }
  return rows;
}

export default function GreenPremiumCalculator() {
  const [lump, setLump] = useState(10000);
  const [monthly, setMonthly] = useState(200);
  const [years, setYears] = useState(20);
  const [rateClassic, setRateClassic] = useState(7.0);
  const [terClassic, setTerClassic] = useState(0.20);
  const [rateSust, setRateSust] = useState(6.8);
  const [terSust, setTerSust] = useState(0.35);

  const { data, endClassic, endSust, diff } = useMemo(() => {
    const c = project(lump, monthly, years, rateClassic, terClassic);
    const s = project(lump, monthly, years, rateSust, terSust);
    const data = c.map((row, i) => ({ year: row.year, "Klassisch": row.value, "Nachhaltig": s[i].value }));
    return { data, endClassic: c[c.length - 1].value, endSust: s[s.length - 1].value, diff: c[c.length - 1].value - s[s.length - 1].value };
  }, [lump, monthly, years, rateClassic, terClassic, rateSust, terSust]);

  const invested = lump + monthly * 12 * years;

  return (
    <div className="border border-neutral-200 bg-white p-8 md:p-10" data-testid="green-premium-calculator">
      <div className="overline mb-3">Rechner · Opportunitätskosten</div>
      <h3 className="font-serif text-3xl md:text-4xl font-medium tracking-tight mb-2">Green Premium Rechner</h3>
      <p className="text-neutral-600 mb-8 max-w-3xl">
        Wie viel Rendite „kostet“ das gute Gewissen? Vergleichen Sie den Endwert eines klassischen Welt-ETF
        mit einem nachhaltigen Pendant (SRI oder Paris-Aligned) – bei individuell einstellbarer
        Rendite und Gesamtkostenquote (TER).
      </p>

      <div className="grid md:grid-cols-2 gap-10">
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-neutral-500 block mb-2">Einmalanlage</label>
              <Input type="number" value={lump} onChange={(e) => setLump(Math.max(0, parseInt(e.target.value) || 0))} className="rounded-none font-mono" data-testid="gp-input-lump" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-neutral-500 block mb-2">Monatliche Sparrate</label>
              <Input type="number" value={monthly} onChange={(e) => setMonthly(Math.max(0, parseInt(e.target.value) || 0))} className="rounded-none font-mono" data-testid="gp-input-monthly" />
            </div>
          </div>

          <div>
            <div className="flex justify-between mb-2">
              <label className="text-sm font-medium text-neutral-700">Anlagehorizont</label>
              <span className="font-mono text-sm text-brand-primary">{years} Jahre</span>
            </div>
            <Slider value={[years]} min={5} max={40} step={1} onValueChange={(v) => setYears(v[0])} data-testid="gp-slider-years" />
          </div>

          <div className="border-t border-neutral-200 pt-6">
            <div className="overline mb-4">Klassisches Portfolio</div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex justify-between mb-2">
                  <label className="text-xs text-neutral-600">Rendite p.a.</label>
                  <span className="font-mono text-xs text-neutral-800">{rateClassic.toFixed(1)} %</span>
                </div>
                <Slider value={[rateClassic]} min={2} max={12} step={0.1} onValueChange={(v) => setRateClassic(v[0])} data-testid="gp-slider-rate-classic" />
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <label className="text-xs text-neutral-600">TER p.a.</label>
                  <span className="font-mono text-xs text-neutral-800">{terClassic.toFixed(2)} %</span>
                </div>
                <Slider value={[terClassic]} min={0} max={2} step={0.05} onValueChange={(v) => setTerClassic(v[0])} data-testid="gp-slider-ter-classic" />
              </div>
            </div>
          </div>

          <div className="border-t border-neutral-200 pt-6">
            <div className="overline mb-4">Nachhaltiges Portfolio</div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex justify-between mb-2">
                  <label className="text-xs text-neutral-600">Rendite p.a.</label>
                  <span className="font-mono text-xs text-neutral-800">{rateSust.toFixed(1)} %</span>
                </div>
                <Slider value={[rateSust]} min={2} max={12} step={0.1} onValueChange={(v) => setRateSust(v[0])} data-testid="gp-slider-rate-sust" />
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <label className="text-xs text-neutral-600">TER p.a.</label>
                  <span className="font-mono text-xs text-neutral-800">{terSust.toFixed(2)} %</span>
                </div>
                <Slider value={[terSust]} min={0} max={2} step={0.05} onValueChange={(v) => setTerSust(v[0])} data-testid="gp-slider-ter-sust" />
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="border border-neutral-200 p-4">
              <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Klassisch</div>
              <div className="font-mono text-lg text-neutral-800" data-testid="gp-end-classic">{eur(endClassic)}</div>
            </div>
            <div className="border border-brand-primary p-4 bg-brand-primary text-white">
              <div className="text-xs uppercase tracking-wider text-white/80 mb-1">Nachhaltig</div>
              <div className="font-mono text-lg" data-testid="gp-end-sust">{eur(endSust)}</div>
            </div>
            <div className="border border-neutral-200 p-4">
              <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Differenz</div>
              <div className={`font-mono text-lg ${diff >= 0 ? "text-neutral-800" : "text-brand-primary"}`} data-testid="gp-diff">{eur(Math.abs(diff))}</div>
              <div className="text-[10px] uppercase tracking-wider text-neutral-500 mt-1">{diff >= 0 ? "Kosten des guten Gewissens" : "Vorsprung nachhaltig"}</div>
            </div>
          </div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <LineChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${(v/1000).toFixed(0)}k` : v)} />
                <Tooltip formatter={(v) => eur(v)} labelFormatter={(l) => `Jahr ${l}`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="Klassisch" stroke="#8B5E3C" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="Nachhaltig" stroke="#3E5743" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 text-xs text-neutral-500">
            Eingezahlt insgesamt: <span className="font-mono">{eur(invested)}</span>
          </div>
        </div>
      </div>
      <p className="text-xs text-neutral-500 mt-6">
        Netto-Rendite = Nominalrendite − TER. Modellrechnung ohne Steuern. Keine Anlageempfehlung.
      </p>
    </div>
  );
}
